import { useCallback, useEffect, useState } from "react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";

import inviteSvg from "@/assets/icons/invite.svg?raw";
import { InlineSvg } from "@/components/ui/inline-svg";

import type {
  InvitationListResponse,
  MemberDetailResponse,
  MemberListResponse,
  SubscriptionResponse,
} from "@/lib/api/generated";
import { UpgradePlanModal } from "@/components/billing/UpgradePlanModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InviteDialog } from "@/components/settings/members/invite-dialog";
import { MembersTable } from "@/components/settings/members/members-table";
import { PendingInvitations } from "@/components/settings/members/pending-invitations";
import { toSubscriptionResponseStatus } from "@/lib/billing/subscription-status";
import { getWorkspaceIdWithFallback } from "@/lib/routing/workspace-context";
import { getSubscription } from "@/server/billing";
import { getCurrentMemberServer, listMembersServer } from "@/server/workspace-members";
import { listInvitationsServer } from "@/server/workspace-invitations";
import { logError } from "@/lib/structured-log";

interface LoaderData {
  workspaceId: string;
  members: MemberListResponse;
  pendingInvitations: InvitationListResponse | null;
  currentMember: MemberDetailResponse;
  subscription: SubscriptionResponse | null;
}

const PAGE_SIZE = 10;

export const Route = createFileRoute("/workspace/$workspaceSlug/settings/workspace/members")({
  loader: async ({ context, parentMatchPromise }): Promise<LoaderData> => {
    const workspaceId = await getWorkspaceIdWithFallback(
      context,
      "settings.workspace.members",
      parentMatchPromise,
    );

    const [members, pendingInvitations, currentMember, subscription] = await Promise.all([
      listMembersServer({ data: { workspaceId, page: 1, pageSize: PAGE_SIZE } }),
      listInvitationsServer({ data: { workspaceId } }).catch(() => null),
      getCurrentMemberServer({ data: { workspaceId } }),
      getSubscription({ data: workspaceId }).catch(() => null),
    ]);

    return { workspaceId, members, pendingInvitations, currentMember, subscription };
  },
  component: MembersPage,
});

function MembersPage() {
  const { t } = useTranslation(["settings"]);
  const {
    workspaceId,
    members: initialMembers,
    pendingInvitations,
    currentMember,
    subscription,
  } = Route.useLoaderData();
  const { workspaceSlug } = Route.useParams();
  const router = useRouter();
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // 分页状态
  const [currentPage, setCurrentPage] = useState(initialMembers.page);
  const [membersData, setMembersData] = useState<MemberListResponse>(initialMembers);
  const [isLoading, setIsLoading] = useState(false);

  // 从 SSR 获取的当前用户信息（数据库 ID 和角色）
  // 确保 user_id 是 string 类型（UUID 可能序列化为对象）
  const currentUserId = String(currentMember.user_id);
  const isOwner = currentMember.role === "workspace_owner";
  const isStarterPlan = subscription?.plan_type?.toLowerCase() === "starter";

  function handleInviteClick() {
    if (isStarterPlan) {
      setShowUpgradeModal(true);
      return;
    }

    setShowInviteDialog(true);
  }

  // 加载成员数据
  const loadMembers = useCallback(
    async (page: number, search: string) => {
      setIsLoading(true);
      try {
        const result = await listMembersServer({
          data: {
            workspaceId,
            page,
            pageSize: PAGE_SIZE,
            search: search || undefined,
          },
        });
        setMembersData(result);
        setCurrentPage(page);
      } catch (error) {
        logError("workspace.members.load.error", {
          error: error instanceof Error ? error.message : "unknown-error",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [workspaceId],
  );

  // 搜索防抖
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // 搜索变化时重新加载
  useEffect(() => {
    // 当搜索关键词变化时，重置到第一页
    loadMembers(1, debouncedSearch);
  }, [debouncedSearch, loadMembers]);

  function handleMembersChange() {
    // 重新加载当前页
    loadMembers(currentPage, debouncedSearch);
    // 同时刷新邀请列表
    router.invalidate();
  }

  function handlePageChange(page: number) {
    loadMembers(page, debouncedSearch);
  }

  return (
    <div className="p-6">
      <div className="w-full">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-[16px] font-semibold text-foreground mb-1">
            {t("workspace.membersPage.title")}
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            {t("workspace.membersPage.description")}
          </p>

          <div className="flex items-center justify-between gap-3">
            <div className="flex h-10 w-[460px] max-w-[min(460px,48vw)] items-center gap-3 rounded-full border border-transparent bg-muted/60 px-3 transition-colors focus-within:border-blue-500">
              <Search className="size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <Input
                  placeholder={t("workspace.membersPage.searchPlaceholder")}
                  className="h-8 min-w-0 border-0 bg-transparent px-0 text-sm shadow-none placeholder:text-muted-foreground focus-visible:ring-0"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            {isOwner && (
              <Button
                onClick={handleInviteClick}
                variant="outline"
                className="h-8 rounded-xl border-[#dfdfdf] bg-white hover:bg-gray-50"
              >
                <InlineSvg content={inviteSvg} size={14} />
                <span className="text-sm text-[#333] font-medium">
                  {isStarterPlan
                    ? t("workspace.membersPage.upgradeToInviteButton")
                    : t("workspace.membersPage.inviteButton")}
                </span>
              </Button>
            )}
          </div>
        </div>

        {/* Members Table */}
        <div className={isLoading ? "opacity-50 pointer-events-none" : ""}>
          <MembersTable
            members={membersData.members}
            workspaceId={workspaceId}
            currentUserId={currentUserId}
            isOwner={isOwner}
            onMembersChange={handleMembersChange}
            page={currentPage}
            pageSize={PAGE_SIZE}
            total={membersData.total}
            onPageChange={handlePageChange}
          />
        </div>

        {/* Pending Invitations - Only visible to owners */}
        {isOwner && pendingInvitations && pendingInvitations.invitations.length > 0 && (
          <div className="mt-8 w-full">
            <h3 className="text-sm font-medium text-gray-900 mb-4">
              {t("workspace.membersPage.invitations.title")}
            </h3>
            <PendingInvitations
              invitations={pendingInvitations.invitations}
              workspaceId={workspaceId}
              onInvitationsChange={handleMembersChange}
            />
          </div>
        )}

        {/* Invite Dialog */}
        <InviteDialog
          open={showInviteDialog}
          onOpenChange={setShowInviteDialog}
          workspaceId={workspaceId}
          onSuccess={handleMembersChange}
        />
        <UpgradePlanModal
          open={showUpgradeModal}
          onOpenChange={setShowUpgradeModal}
          workspaceId={workspaceId}
          workspaceSlug={workspaceSlug}
          currentPlan={subscription?.plan_type}
          subscriptionStatus={toSubscriptionResponseStatus(subscription?.status)}
          defaultSelectedPlan="professional"
        />
      </div>
    </div>
  );
}
